import { useState } from 'react';
import { Shield } from 'lucide-react';
import { useAdminLogin } from '@/hooks/use-auth';
import { PasswordInput } from '@/components/ui/password-input';
import { TwoFactorVerify } from './two-factor-verify';

export function AdminLoginPage() {
  const login = useAdminLogin();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [pending2FA, setPending2FA] = useState<{ pendingToken: string } | null>(null);

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Please enter a valid email';
    if (!password) errors.password = 'Password is required';
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      const result = await login.mutateAsync({ email, password });
      if (result.requires2FA) {
        setPending2FA({ pendingToken: result.pendingToken });
        return;
      }
      window.location.hash = '#/admin';
    } catch (err: any) {
      setError(err?.error?.message || 'Login failed');
    }
  };

  if (pending2FA) {
    return (
      <TwoFactorVerify
        pendingToken={pending2FA.pendingToken}
        isAdmin={true}
        onBack={() => {
          setPending2FA(null);
          setPassword('');
        }}
      />
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gray-50">
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none absolute -left-32 -bottom-32 h-[500px] w-[500px] rounded-full bg-blue-400/30 blur-[120px]" />
      <div className="pointer-events-none absolute -right-32 -top-32 h-[500px] w-[500px] rounded-full bg-teal-400/20 blur-[120px]" />
      <div className="relative z-10 w-full max-w-sm lg:max-w-[430px] space-y-6 rounded-xl border border-border bg-white/80 p-8 shadow-lg backdrop-blur-sm">
        <div className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Admin Portal</h1>
          <p className="mt-1 text-sm text-muted-foreground">Q PaymentZ Back Office</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

          <div>
            <label className="mb-1 block text-sm font-medium">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setFieldErrors((prev) => ({ ...prev, email: '' }));
              }}
              autoFocus
              className={`h-10 w-full rounded-lg border bg-background px-3 text-sm outline-none focus:ring-1 ${fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
              placeholder="admin@fintech.dev"
            />
            {fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldErrors((prev) => ({ ...prev, password: '' }));
              }}
              className={`h-10 w-full rounded-lg border bg-background px-3 text-sm outline-none focus:ring-1 ${fieldErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input focus:border-primary focus:ring-primary'}`}
              placeholder="Enter your password"
            />
            {fieldErrors.password && <p className="mt-1 text-xs text-red-500">{fieldErrors.password}</p>}
          </div>

          <button
            type="submit"
            disabled={login.isPending}
            className="h-10 w-full rounded-lg bg-primary text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {login.isPending ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}
